import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = user
    ? [
        { name: 'Dashboard', href: '/dashboard' },
        { name: 'Plans', href: '/plans' },
        { name: 'Account', href: '/account' },
      ]
    : [
        { name: 'Home', href: '/' },
        { name: 'Products', href: '/products' },
        { name: 'About', href: '/about' },
        { name: 'Blog', href: '/blog' },
        { name: 'Support', href: '/support' },
        { name: 'Contact', href: '/contact' },
      ];

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
      <motion.nav
        className={`max-w-4xl mx-auto rounded-3xl transition-all duration-300 ${
          isScrolled
            ? 'bg-secondary-50/95 dark:bg-dark-800/95 shadow-warm-lg border border-primary-200/20 dark:border-primary-900/20'
            : 'bg-secondary-50/80 dark:bg-dark-800/80 border border-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" onClick={handleNavClick}>
              <motion.div
                className="flex items-center gap-2 cursor-pointer"
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              >
                <img
                  src={theme === 'light' ? '/bioaxis-logo-colour.png' : '/bioaxis-logo.png'}
                  alt="BioAxis Logo"
                  className="w-7 h-7 rounded-lg"
                />
                <span className="text-lg font-bold text-primary-600 dark:text-primary-400">BioAxis</span>
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  className="relative"
                >
                  <motion.span
                    className={`block px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                      location.pathname === item.href
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-dark-600 dark:text-dark-300 hover:text-dark-900 dark:hover:text-dark-100'
                    }`}
                    whileHover={{ y: -1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    {item.name}
                  </motion.span>
                </Link>
              ))}
            </div>

            {/* Right side: theme toggle + CTA */}
            <div className="hidden md:flex items-center gap-3">
              <ThemeToggle />
              {user ? (
                <button
                  className="btn-primary text-sm px-5 py-2"
                  onClick={async () => { await signOut(); handleNavClick(); }}
                >
                  <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="block">
                    Log Out
                  </motion.span>
                </button>
              ) : (
                <Link
                  to="/contact"
                  onClick={handleNavClick}
                  className="btn-primary text-sm px-5 py-2 inline-block"
                >
                  <motion.span
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="block"
                  >
                    Get Demo
                  </motion.span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="md:hidden p-2 rounded-full bg-primary-50 dark:bg-dark-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 text-dark-700 dark:text-dark-200" />
              ) : (
                <Menu className="w-5 h-5 text-dark-700 dark:text-dark-200" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden border-t border-primary-200/20 dark:border-primary-900/15"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-4 py-4 space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={`block px-4 py-2.5 rounded-2xl text-sm font-medium transition-colors duration-200 ${
                      location.pathname === item.href
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-dark-600 dark:text-dark-300 hover:text-dark-900 dark:hover:text-dark-100'
                    }`}
                  >
                    <motion.span
                      whileHover={{ x: 8 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                      className="block"
                    >
                      {item.name}
                    </motion.span>
                  </Link>
                ))}

                <div className="flex items-center justify-center py-2">
                  <ThemeToggle />
                </div>

                {user ? (
                  <button
                    onClick={async () => { await signOut(); handleNavClick(); }}
                    className="btn-primary w-full text-sm mt-2"
                  >
                    <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="block">
                      Log Out
                    </motion.span>
                  </button>
                ) : (
                  <div className="flex flex-col gap-2 mt-2">
                    <Link
                      to="/login"
                      onClick={handleNavClick}
                      className="btn-secondary w-full text-center text-sm"
                    >
                      <motion.span
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="block"
                      >
                        Login
                      </motion.span>
                    </Link>
                    <Link
                      to="/contact"
                      onClick={handleNavClick}
                      className="btn-primary w-full text-center text-sm"
                    >
                      <motion.span
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="block"
                      >
                        Get Demo
                      </motion.span>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </div>
  );
};

export default Navbar;
